import moment from 'moment/moment'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { twMerge as twm } from 'tailwind-merge'

import { Block } from '../../models/Block'
import { RecordFactory } from '../../models/RecordFactory'
import { Schedule } from '../../models/Schedule'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { PERM_ROLE_ADMIN } from '../../utils'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { DatePicker } from '../common/fields/DatePicker'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextField } from '../common/fields/TextField'
import {
    blockCountOptions,
    blockDurationOptions,
    recurringScheduleFrequencyOptions,
    timeHourOptions,
    timeMinuteIntervalFiveOptions,
} from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async ({
    urlParams,
    owner,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' }>) => {
    const defaultBlock = await (async (): Promise<Block | null> => {
        switch (action) {
            case 'new': {
                return new Block({ owner, date: moment(), person: PSApplication.me() })
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().fetchRecord(urlParams.id, Block)) ?? null
            }
            default:
                return null
        }
    })()
    if (defaultBlock === null) {
        return new RecordNotFoundError()
    }

    return { action, defaultBlock }
}

const render = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = useNavigate()

    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { action, defaultBlock } = data

    const [block, setBlock] = useState(defaultBlock)

    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] = useState(false)
    const [isShowingEditConfirmation, setIsShowingEditConfirmation] = useState(false)

    const me = PSApplication.me()
    const canDelete =
        !!block.id &&
        (me.perm >= PERM_ROLE_ADMIN || me.id === block.owner?.id || me.id === block.creator?.id)
    const canSave = !!block.title && !!block.date && !!block.duration

    const isRepeatEnabled = block.schedule !== undefined
    const handleChangeIsRepeatEnabled = (isEnabled: boolean) => {
        if (isEnabled) {
            setBlock(block.clone({ schedule: new Schedule() }))
        }
        else {
            setBlock(block.clone({ schedule: undefined }))
        }
    }

    const currentRepeatIntervalValue = block.schedule
        ? `${block.schedule.interval}${block.schedule.frequency === 'monthly' ? 'm' : ''}`
        : undefined
    const handleSelectRepeatFrequencyOption = (option: { value: string } | null) => {
        if (option === null) {
            return
        }

        const schedule = block.schedule
        if (schedule === undefined) {
            return
        }

        const interval = parseInt(option.value)
        const frequency = option.value.endsWith('m') ? 'monthly' : 'weekly'

        setBlock(block.clone({ schedule: schedule.clone({ interval, frequency }) }))
    }

    const handleToggleRepeatingDayOfWeek = (dayValue: number) => {
        const schedule = block.schedule
        if (schedule === undefined) {
            return
        }

        if (Boolean((schedule.dow ?? 0) & dayValue)) {
            setBlock(
                block.clone({
                    schedule: schedule.clone({ dow: (schedule.dow ?? 0) & ~dayValue }),
                })
            )
        }
        else {
            setBlock(
                block.clone({
                    schedule: schedule.clone({ dow: (schedule.dow ?? 0) | dayValue }),
                })
            )
        }
    }

    const dateDayName = moment(block.date).format('dddd')

    const handleDelete = async (deleteAll: boolean) => {
        if (!block.id) return

        setIsDeleting(true)
        const result = await RecordFactory.getInstance().deleteBlock(block.id, deleteAll)
        setIsDeleting(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(-1)
    }

    const handleSave = async (changeAll: boolean) => {
        setIsSaving(true)
        const result = await RecordFactory.getInstance().sendBlock(block, changeAll)
        setIsSaving(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(-1)
    }

    const hourItems = timeHourOptions.map(({ value, label }) => ({
        id: parseInt(value).toString(),
        title: label,
        value: parseInt(value),
    }))
    const minuteItems = timeMinuteIntervalFiveOptions.map(({ value, label }) => ({
        id: parseInt(value).toString(),
        title: label,
        value: parseInt(value),
    }))
    const durationItems = blockDurationOptions.map(({ value, label }) => ({
        id: parseInt(value).toString(),
        title: label,
        value: parseInt(value),
    }))
    const recurringScheduleFrequencyItems = recurringScheduleFrequencyOptions.map(
        ({ value, label }) => ({
            id: value,
            title: label,
            value,
        })
    )
    const blockCountItems = blockCountOptions.map(({ value, label }) => ({
        id: value === '' ? '' : parseInt(value).toString(),
        title: label,
        value: value === '' ? undefined : parseInt(value),
    }))

    return (
        <WorkflowPage
            title={
                action === 'new'
                    ? 'New block'
                    : action === 'edit'
                      ? 'Edit block'
                      : 'Edit all blocks'
            }
            onClose={{
                action: 'go-back',
                excludePathname: /^\/blocks.*\/(new|edit)$/,
                defaultPathname: '/calendar',
            }}
            footerContent={
                <div
                    className={twm(
                        'flex flex-row items-center justify-between gap-x-4',
                        !block.id ? 'justify-end' : 'justify-between'
                    )}
                >
                    {canDelete && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            isLoading={isDeleting}
                            onClick={() => setIsShowingDeleteConfirmation(true)}
                        >
                            Delete block
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        isLoading={isSaving}
                        disabled={!canSave}
                        onClick={
                            !block.id || !defaultBlock.schedule
                                ? () => handleSave(isRepeatEnabled)
                                : () => setIsShowingEditConfirmation(true)
                        }
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General" className="w-full">
                    <WorkflowPage.IconSection title="Title (required)" icon="clockLock.large">
                        <TextField
                            value={block.title ?? ''}
                            onChange={(event) =>
                                setBlock(block.clone({ title: event.target.value }))
                            }
                            onClear={() => setBlock(block.clone({ title: '' }))}
                            size="medium"
                            placeholder="Title"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Date (required)" icon="calendar.large">
                        <DatePicker
                            selectedDateString={block.date?.toISOString()}
                            onSelectDate={(newDate) =>
                                setBlock(block.clone({ date: moment(newDate) }))
                            }
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Time (required)" icon="clock.large">
                        <div className="grid grid-cols-2 grid-rows-1 gap-x-4">
                            <SearchableSelect
                                items={hourItems}
                                selectedItemID={moment(block.date).hours().toString()}
                                onSelectItem={(option) =>
                                    setBlock(
                                        block.clone({
                                            date: moment(block.date).hours(option?.value ?? 0),
                                        })
                                    )
                                }
                                size="medium"
                                shouldPreserveLastSelection
                            />

                            <SearchableSelect
                                items={minuteItems}
                                selectedItemID={moment(block.date).minutes().toString()}
                                onSelectItem={(option) =>
                                    setBlock(
                                        block.clone({
                                            date: moment(block.date).minutes(option?.value ?? 0),
                                        })
                                    )
                                }
                                size="medium"
                                shouldPreserveLastSelection
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Duration (required)" icon="stopwatch.large">
                        <SearchableSelect
                            items={durationItems}
                            selectedItemID={block.duration?.toString()}
                            onSelectItem={(option) =>
                                setBlock(block.clone({ duration: option?.value ?? undefined }))
                            }
                            size="medium"
                            shouldPreserveLastSelection
                        />
                    </WorkflowPage.IconSection>

                    {owner.allowRecurring && (!block.id || !!block.schedule) && (
                        <WorkflowPage.IconSection
                            title="Repeat"
                            icon="repeat.large"
                            description={
                                !!block.id && !!block.schedule
                                    ? block.schedule.description
                                    : undefined
                            }
                            secondaryView={
                                <Checkbox
                                    isChecked={isRepeatEnabled}
                                    onChange={handleChangeIsRepeatEnabled}
                                    disabled={!!block.id && !!block.schedule}
                                />
                            }
                            disabled={!!block.id && !!block.schedule}
                            hasHoverEffect
                        >
                            {isRepeatEnabled && !block.id && block.schedule !== undefined && (
                                <div className="flex flex-col items-stretch justify-start gap-y-8">
                                    <div>
                                        <Label size="md">Repeat every</Label>
                                        <SearchableSelect
                                            items={recurringScheduleFrequencyItems}
                                            selectedItemID={currentRepeatIntervalValue}
                                            onSelectItem={handleSelectRepeatFrequencyOption}
                                            size="medium"
                                            className="mt-2"
                                            shouldPreserveLastSelection
                                        />
                                    </div>

                                    {block.schedule.frequency === 'weekly' ? (
                                        <div>
                                            <Label size="md">Days of the week</Label>

                                            <div className="mt-2 flex flex-wrap items-center justify-start gap-x-2 gap-y-2">
                                                {[
                                                    'Monday',
                                                    'Tuesday',
                                                    'Wednesday',
                                                    'Thursday',
                                                    'Friday',
                                                    'Saturday',
                                                    'Sunday',
                                                ].map((dayName, dayIndex) => {
                                                    const isSelected = Boolean(
                                                        (block.schedule?.dow ?? 0) &
                                                            Math.pow(2, dayIndex)
                                                    )

                                                    return (
                                                        <Chip
                                                            key={dayName}
                                                            title={dayName}
                                                            isSelected={isSelected}
                                                            onClick={() =>
                                                                handleToggleRepeatingDayOfWeek(
                                                                    Math.pow(2, dayIndex)
                                                                )
                                                            }
                                                            onRemove={
                                                                isSelected
                                                                    ? () =>
                                                                          handleToggleRepeatingDayOfWeek(
                                                                              Math.pow(2, dayIndex)
                                                                          )
                                                                    : undefined
                                                            }
                                                            size="small"
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <Label size="md">Day of the month</Label>

                                            <div className="mt-2 flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                                                <Chip
                                                    title={`First ${dateDayName}`}
                                                    isSelected={(block.schedule?.wom ?? 1) === 1}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: 1,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <Chip
                                                    title={`Second ${dateDayName}`}
                                                    isSelected={block.schedule?.wom === 2}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: 2,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <Chip
                                                    title={`Third ${dateDayName}`}
                                                    isSelected={block.schedule?.wom === 3}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: 3,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <Chip
                                                    title={`Fourth ${dateDayName}`}
                                                    isSelected={block.schedule?.wom === 4}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: 4,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <Chip
                                                    title={`Last ${dateDayName}`}
                                                    isSelected={block.schedule?.wom === -1}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: -1,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                                <Chip
                                                    title={`Second to last ${dateDayName}`}
                                                    isSelected={block.schedule?.wom === -2}
                                                    onClick={() =>
                                                        setBlock(
                                                            block.clone({
                                                                schedule: block.schedule?.clone({
                                                                    wom: -2,
                                                                }),
                                                            })
                                                        )
                                                    }
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <Label size="md">Number of times</Label>
                                        <SearchableSelect
                                            items={blockCountItems}
                                            selectedItemID={(
                                                block.schedule?.instances ?? ''
                                            ).toString()}
                                            onSelectItem={(option) =>
                                                setBlock(
                                                    block.clone({
                                                        schedule: block.schedule?.clone({
                                                            instances: option?.value ?? undefined,
                                                        }),
                                                    })
                                                )
                                            }
                                            size="medium"
                                            className="mt-2"
                                            shouldPreserveLastSelection
                                        />
                                    </div>
                                </div>
                            )}
                        </WorkflowPage.IconSection>
                    )}
                </WorkflowPage.Card>
            </div>

            <ConfirmationModal
                isOpen={isShowingDeleteConfirmation}
                title="Are you sure?"
                size={!!defaultBlock.schedule ? 'medium' : 'small'}
                onClose={() => setIsShowingDeleteConfirmation(false)}
                onCancel={!!defaultBlock.schedule ? () => handleDelete(true) : undefined}
                onConfirm={() => handleDelete(false)}
                cancelButtonText={
                    !!defaultBlock.schedule ? 'Delete this and future blocks' : 'Cancel'
                }
                confirmButtonText={!!defaultBlock.schedule ? 'Delete this block' : 'Delete'}
                isDestructive
            >
                <Paragraph>
                    {!!defaultBlock.schedule
                        ? 'This block repeats on your calendar. If you would like to delete just this block, select "Delete this block". If you would like to delete this and future blocks, choose "Delete this and future blocks".'
                        : 'Would you like to remove this block from your calendar?'}
                </Paragraph>
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={isShowingEditConfirmation}
                title="Edit"
                size="medium"
                onClose={() => setIsShowingEditConfirmation(false)}
                onCancel={() => handleSave(true)}
                onConfirm={() => handleSave(false)}
                cancelButtonText="Edit this and future blocks"
                confirmButtonText="Edit this block"
            >
                <Paragraph>
                    This block repeats on your calendar. If you would like to edit just this block,
                    select "Edit this block". If you would like to edit this and future blocks,
                    choose "Edit this and future blocks".
                </Paragraph>
            </ConfirmationModal>
        </WorkflowPage>
    )
}

export const BlockSchedule = usePSComponent(render, viewWillAppear)
